import React, { useEffect, useState, useRef } from "react";
import ReactToPrint from "react-to-print";
import BootstrapTable from "react-bootstrap-table-next";
import {
  priceFormatter,
  withIcon,
  formatDate,
  fullname,
  nickname,
} from "./violationHelpers";
import { useDispatch, useSelector } from "react-redux";
import PatientProfileHeaderPrint from "../../Reusable/PatintProfileHeaderPrint";
import LottieLoader from '../../../components/LottieLoader';
import lottieEmptyFile from '../../../files/animation/empty_new.json';
export const ViolationListPrint = ({
  isAdminPatient,
  isClientPatient,
  isCareProviderPatient,
  isCareProvider,
  patientId,
  filterData,
  display,
  disabled,
  CSVTitle
}) => {
  const [violationData, setViolationData] = useState([]);
  const [printPending, setPrintPending] = useState(false);
  const componentRef = useRef();
  const dispatch = useDispatch();
  const reactToPrintTriggerRef = useRef();
  const { ongoingUserViolationRequestPrint, usersViolationsPrintData, latestReadingUser, ccmTotalDuration, rpmTotalDuration } = useSelector(
    (state) => state.careprovider
  );
  const { profile, userDetails } = useSelector(
    (state) => state.user
  );
  const { startDate, endDate } = filterData;
  const getViolationList = () => {
    let payload = filterData;
    if ((isAdminPatient || isClientPatient || isCareProviderPatient) && patientId) {
      payload = { ...payload, patientId };
    }

    const type = "DO_GET_USER_VIOLATIONS_PRINT";

    if (isAdminPatient || isClientPatient || isCareProviderPatient) {
      payload.usersViolations = true;
    } else {
      payload.myViolations = true;
    }

    dispatch({ type, payload });
  };

  const handlePrintBtnClick = () => {
    setPrintPending(true);
    getViolationList();    // fetch fresh data
  };

  // Trigger the print only when new data arrives and flag is set
  useEffect(() => {
    if (usersViolationsPrintData && !ongoingUserViolationRequestPrint) {
      setViolationData(usersViolationsPrintData);
    }
  }, [usersViolationsPrintData, ongoingUserViolationRequestPrint]);

  useEffect(() => {
  if(violationData && printPending){
    if (reactToPrintTriggerRef.current) {
        reactToPrintTriggerRef.current.handlePrint();
    }
    setPrintPending(false); // Reset flag
  }
  }, [violationData])
  const handleAfterPrint = () => {
    setViolationData([]);
    document.title = 'MYVITALVIEW';
  };
  const handleBeforePrint = () => {
    if(CSVTitle === 'Events&Exceptions'){
       document.title = `Remote Patient Monitoring Events and Exceptions Report (${profile.fname} ${profile.lname})`;
    }else if(CSVTitle === 'Events Exceptions'){
      document.title = `Remote Patient Monitoring Events Report (${profile.fname} ${profile.lname})`
    }else if(CSVTitle === 'Vitals Exceptions'){
      document.title = `Remote Patient Monitoring Vitals Exceptions Report (${profile.fname} ${profile.lname})`
    }else if(CSVTitle === 'Adherences Exceptions'){
      document.title = `Remote Patient Monitoring Adherence Exceptions Report (${profile.fname} ${profile.lname})`
    }else if(CSVTitle === 'Questionnaires Exceptions'){
      document.title = `Remote Patient Monitoring Questionnaires Exceptions Report (${profile.fname} ${profile.lname})`
    }else{
      document.title = CSVTitle;
    }
  };
  return (
    <>
      {/* Manual trigger button */}
      <button
        className="btn btn-info print-click-cls m-0"
        disabled={ongoingUserViolationRequestPrint}
        onClick={handlePrintBtnClick}
      >
        {ongoingUserViolationRequestPrint ? (
          <i className="mdi mdi-loading mdi-spin mr-2" style={{ fontSize: 20 }}></i>
        ) : (
          <i className="mdi mdi-cloud-print" style={{ fontSize: 17 }}></i>
        )}
        Print
      </button>

      {/* Hidden ReactToPrint trigger */}
      <ReactToPrint
        trigger={() => <div />}
        content={() => componentRef.current}
        onAfterPrint={() => handleAfterPrint()}
        onBeforePrint={() => handleBeforePrint()}
        ref={reactToPrintTriggerRef}
      />
      <style>
      {`
        @media print {
          @page {
            margin: 4%;
          }
        }
      `}
      </style> 
      <div className="text-center" style={{ display: "none" }}>
        <div ref={componentRef}>
        <div className="print-page-title">Required Documentation for CPT Code 99454</div>
          <PatientProfileHeaderPrint
          profile={profile}
          latestReadingUser={latestReadingUser}
          ccmTotalDuration = {ccmTotalDuration}
          rpmTotalDuration = {rpmTotalDuration}
          userDetails = {userDetails}>
          </PatientProfileHeaderPrint>

          {/* Date Range Display */}
          {(startDate || endDate) && (
            <div
              className="d-flex mb-2 mt-3"
              style={{
                fontSize: "16px",
                fontWeight: "500",
                maxWidth: "400px",
              }}
            >
              <span style={{ marginRight: "8px", color: "#333" }}>Filtered Date Range:</span>
              <span style={{ fontWeight: "bold", color: "#000" }}>
                {startDate ? new Date(startDate).toLocaleDateString() : "—"}{" "}
                <span style={{ color: "#888" }}>to</span>{" "}
                {endDate ? new Date(endDate).toLocaleDateString() : "—"}
              </span>
            </div>
          )}

          <BootstrapTable
            wrapperClasses="table-responsive"
            data={violationData && violationData.length > 0 ? violationData : []}
            keyField="_id"
            noDataIndication={() => (
              <div>
                <LottieLoader
                    loadLoop={false}
                    autoplayLoop={true}
                    source={lottieEmptyFile}
                    Lheight={200}
                    Lwidth={200}
                    titleSmall={"Sorry! No exceptions found"}
                    subTitleSmall={"We can't find any exceptions data for this patient."}
                  />
              </div>
            )}
            columns={[
              {
                dataField: "name",
                text: "Title",
                headerFormatter: priceFormatter,
                formatter: (cell, row) =>
                  row?.isRead ? row.name : <strong>{row?.name || ""}</strong>,
              },
              ...(display !== "Questionnaires"
                ? [
                    {
                      dataField: "readingSubType",
                      text: "Reading Sub Type",
                      headerFormatter: priceFormatter,
                      formatter: (cell) => cell || "-",
                    },
                  ]
                : []),
              ...(display !== "Questionnaires"
                ? [
                    {
                      dataField: "ruleType",
                      text: "Rule Type",
                      formatter: withIcon,
                      headerFormatter: priceFormatter,
                    },
                  ]
                : []),
              {
                dataField: "violatedBy",
                text: "Patient",
                formatter: fullname,
                headerFormatter: priceFormatter,
              },
              {
                dataField: "violatedBy.nickname",
                text: "Patient Nickname",
                formatter: nickname,
                headerFormatter: priceFormatter,
              },
              {
                dataField: "createdAt",
                text: "Occurred",
                formatter: formatDate,
                headerFormatter: priceFormatter,
              },
            ]}
          />
        </div>
      </div>
    </>
  );
};
